import { useTranslation } from 'react-i18next';

import DateInput from '@/components/ui-v2/date/date-input';
import {
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getVatLabelKey } from '@/lib/utils';
import { storeAtom } from '@/states/store.state';
import { Currency } from '@/types/currency';
import { Store } from '@/types/store';
import { useAtomValue } from 'jotai';
import { ArrowLeft } from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { BaseImportViewProps } from './import-view-props';

type CouponForm = {
  name: string;
  gtin: string;
  listPrice: string;
  choosePrice: string;
  description: string;
  expiration: string;
  experienceDeadline: string;
};

interface ViewCouponsManualProps extends BaseImportViewProps {
  couponForm: CouponForm;
  setCouponForm: (
    form: CouponForm | ((prev: CouponForm) => CouponForm)
  ) => void;
  canCreateCoupon: boolean;
  creatingCoupon: boolean;
}

export const ViewCouponsManual: React.FC<ViewCouponsManualProps> = ({
  setImportView,
  couponForm,
  setCouponForm,
  canCreateCoupon,
  creatingCoupon,
}) => {
  const store = useAtomValue(storeAtom);
  const { t } = useTranslation();
  const vatLabel = t(getVatLabelKey(store));
  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-2">
          <button
            className="rounded-md p-1 hover:bg-muted"
            onClick={() => setImportView('coupons')}
            aria-label={t(
              'components.import_flow.coupons_manual.back_to_coupons'
            )}
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <DialogTitle>
            {t('components.import_flow.coupons_manual.title')}
          </DialogTitle>
        </div>
        <DialogDescription>
          {t('components.import_flow.coupons_manual.description')}
        </DialogDescription>
      </DialogHeader>
      <div className="flex min-h-0 flex-grow flex-col space-y-4 py-4">
        <div className="flex-grow overflow-y-auto px-4 py-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium leading-5">
                {t('kpi.name')} <span className="text-red-600"> *</span>
              </label>
              <Input
                className="h-12"
                placeholder={t(
                  'components.import_flow.coupons_manual.name_placeholder'
                )}
                value={couponForm.name}
                onChange={(e) =>
                  setCouponForm((s) => ({ ...s, name: e.target.value }))
                }
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium leading-5">
                {t('kpi.gtin')} <span className="text-red-600"> *</span>
              </label>
              <Input
                className="h-12"
                placeholder={t(
                  'components.import_flow.coupons_manual.gtin_placeholder'
                )}
                value={couponForm.gtin}
                onChange={(e) =>
                  setCouponForm((s) => ({ ...s, gtin: e.target.value }))
                }
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium leading-5">
                {t('kpi.list_price')} ({vatLabel})
                <span className="text-red-600"> *</span>
              </label>
              <div className="relative">
                <Input
                  className="h-12 pr-8"
                  type="number"
                  step="0.01"
                  value={couponForm.listPrice}
                  onChange={(e) =>
                    setCouponForm((s) => ({
                      ...s,
                      listPrice: e.target.value,
                    }))
                  }
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                  {store === Store.FR ? Currency.EUR : Currency.USD}
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium leading-5">
                {t('kpi.choose_price')} ({vatLabel}){' '}
                <span className="text-red-600"> *</span>
              </label>
              <div className="relative">
                <Input
                  className="h-12 pr-8"
                  type="number"
                  step="0.01"
                  value={couponForm.choosePrice}
                  onChange={(e) =>
                    setCouponForm((s) => ({
                      ...s,
                      choosePrice: e.target.value,
                    }))
                  }
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                  {store === Store.FR ? Currency.EUR : Currency.USD}
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-2 md:col-span-2">
              <label className="text-sm font-medium leading-5">
                {t('kpi.description')}
                <span className="text-red-600"> *</span>
              </label>
              <Textarea
                placeholder={t(
                  'components.import_flow.coupons_manual.description_placeholder'
                )}
                value={couponForm.description}
                onChange={(e) =>
                  setCouponForm((s) => ({
                    ...s,
                    description: e.target.value,
                  }))
                }
                rows={4}
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium leading-5">
                {t('kpi.coupon_expiration_date')}
                <span className="text-red-600"> *</span>
              </label>
              <DateInput
                className="h-12"
                value={couponForm.expiration || null}
                onDateChange={(iso: string | null) =>
                  setCouponForm((s) => ({
                    ...s,
                    expiration: iso ?? '',
                    experienceDeadline: iso ?? '',
                  }))
                }
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium leading-5">
                {t('kpi.coupon_date_before_null')}
                <span className="text-red-600"> *</span>
              </label>
              <DateInput
                className="h-12"
                value={couponForm.experienceDeadline || null}
                onDateChange={(iso: string | null) =>
                  setCouponForm((s) => ({
                    ...s,
                    experienceDeadline: iso ?? '',
                  }))
                }
              />
            </div>
          </div>
        </div>
        <div className="flex-shrink-0 border-t pt-4">
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setImportView('coupons')}
              disabled={creatingCoupon}
            >
              {t('general.previous')}
            </Button>
            <Button
              onClick={() => {
                if (!canCreateCoupon) return;
                setImportView('couponsCGU');
              }}
              disabled={!canCreateCoupon || creatingCoupon}
            >
              {t('general.next')}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};
